import { useCallback } from "react";
import { useLoggedIn } from "../loginHelpers";
import { useAppState } from "./useAppState";
import { useAppContext } from "./useAppContext";
import { ActionType, type MctProvider } from "../../@types";
import { useQueryClient } from "@tanstack/react-query";
import { useBeneinfoProvidersQueryKey } from "../query-hooks/queryKeyHooks";
import { useBeneinfoProviders } from "../query-hooks/pd/useBeninfoProviders";

/**
 * Returns a bene's saved providers and callbacks for adding and removing providers
 *
 * For authenticated benes, updates made to the cached queryClient value also
 * synchronizes with `AppState.providers`, since that's the context for the providers
 * referenced in the UI
 *
 * Other RQ hook values are also returned
 */
export const useProviders = () => {
  const { anonProviders } = useAppState();
  const { dispatch } = useAppContext();
  const isLoggedIn = useLoggedIn();
  const queryClient = useQueryClient();
  const beneinfoProvidersQueryKey = useBeneinfoProvidersQueryKey();

  // * Queries
  const {
    providers: beneinfoProviders,
    error,
    isError,
    isLoading,
  } = useBeneinfoProviders();

  // * Constants
  const providers = isLoggedIn ? beneinfoProviders : anonProviders;

  // * Callbacks
  const addProvider = useCallback(
    (provider: MctProvider) => {
      if (isLoggedIn) {
        queryClient.setQueryData(beneinfoProvidersQueryKey, [
          ...(beneinfoProviders ?? []),
          provider,
        ]);
      } else {
        dispatch({
          type: ActionType.ADD_PROVIDER,
          payload: provider,
        });
      }
    },
    [
      beneinfoProviders,
      beneinfoProvidersQueryKey,
      dispatch,
      isLoggedIn,
      queryClient,
    ]
  );
  const getProviderFromNpi = useCallback(
    (npi: string) => providers.find(p => p.npi === npi),
    [providers]
  );
  const removeProvider = useCallback(
    (provider: MctProvider) => {
      if (isLoggedIn) {
        queryClient.setQueryData(
          beneinfoProvidersQueryKey,
          (beneinfoProviders ?? [])?.filter(p => p.npi !== provider.npi)
        );
      } else {
        dispatch({
          type: ActionType.DELETE_PROVIDER,
          payload: `${provider.npi}`,
        });
      }
    },
    [
      beneinfoProviders,
      beneinfoProvidersQueryKey,
      dispatch,
      isLoggedIn,
      queryClient,
    ]
  );

  return {
    addProvider,
    error,
    getProviderFromNpi,
    isError,
    isLoading,
    providers,
    removeProvider,
  };
};
